import { type UseMutationResult, useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SectionResponse } from '../types/api';
import type { CustomerMinimal } from '../types/company';
import { type SectionTreeNodeTypeID, sectionTreeNodeIDs } from '../types/section-tree';
import { useDeleteSectionById, useUpdateSectionById } from './use-sections';

export const openHandleID: string = 'open';
const mainRoleID: string = '2abec3c1-74f2-4881-affa-07d4d6e9769d';

type SectionTreeMutations = {
  floatingCustomers: CustomerMinimal[];
  sectionChildren: Record<string, string[]>;
  sectionUsers: Record<string, string[]>;
  useAddCompanyUser: UseMutationResult<
    void,
    Error,
    { sectionId: string | null; customer: CustomerMinimal | string },
    unknown
  >;
  useRemoveNode: UseMutationResult<void, Error, string, unknown>;
  useRemoveEdge: UseMutationResult<
    void,
    Error,
    { parentNodeId: string; handleId: string; childNodeId: string },
    unknown
  >;
  useConnect: UseMutationResult<
    void,
    Error,
    { parentNodeId: string; handleId: string; childNodeId: string },
    unknown
  >;
  isValidConnection: (parentNodeId: string, handleId: string, childNodeId: string) => boolean;
};

export const useSectionTreeMutations = (
  companyId: string,
  sections: SectionResponse[],
): SectionTreeMutations => {
  const updateSection = useUpdateSectionById(companyId);
  const deleteSection = useDeleteSectionById(companyId);

  // Stores the list of customers that were recently attached to sections but are no longer attached
  const [floatingCustomers, setFloatingCustomers] = useState<CustomerMinimal[]>([]);

  // Ensure that customers are removed from floating list when sections changes
  useEffect(() => {
    const nonFloatingIds = new Set(
      sections.flatMap(({ company_users }) => company_users.map(({ customer }) => customer.id)),
    );
    setFloatingCustomers((current) => {
      const filtered = current.filter(({ id }) => !nonFloatingIds.has(id));
      // Only return a new array if items have been removed
      return current.length === filtered.length ? current : filtered;
    });
  }, [sections]);

  const sectionChildren = useMemo(
    () =>
      sections.reduce(
        (a, x) => {
          if (x.parent_id !== null) {
            a[x.parent_id] = a[x.parent_id]?.concat(x.id) ?? [x.id];
          }
          return a;
        },
        {} as Record<string, string[]>,
      ),
    [sections],
  );

  const sectionUsers = sections.reduce(
    (a, x) => {
      a[x.id] = x.company_users.map(({ customer }) => customer.id);
      return a;
    },
    {} as Record<string, string[]>,
  );

  const addFloatingCustomer = useCallback(
    (customer: CustomerMinimal) =>
      setFloatingCustomers((current) => {
        // Only add customer to floating customers if there's no other sections with that customer
        const customerInUse = sections
          .flatMap(({ company_users }) => company_users)
          .some(({ customer: { id } }) => id === customer.id);
        return customerInUse
          ? current
          : current.filter(({ id }) => id !== customer.id).concat(customer);
      }),
    [sections],
  );

  const useAddCompanyUser = useMutation({
    mutationFn: async ({
      sectionId,
      customer,
    }: {
      sectionId: string | null;
      customer: CustomerMinimal | string;
    }) => {
      const section = sectionId === null ? null : sections.find(({ id }) => id === sectionId);
      if (!section && typeof customer === 'object') {
        addFloatingCustomer(customer);
        return;
      }
      if (!section) {
        console.warn('Cannot add company user by ID alone if no section ID provided');
        return;
      }
      await updateSection.mutateAsync({
        ...section,
        company_users: section.company_users
          .map((cu) => ({ customer_id: cu.customer.id, role_id: cu.role.id }))
          .concat({
            customer_id: typeof customer === 'object' ? customer.id : customer,
            role_id: mainRoleID,
          }),
      });
    },
  });

  const useRemoveNode = useMutation({
    mutationFn: async (nodeID: string) => {
      const n = trimNodeID(nodeID);
      if (!n) {
        console.warn('cannot remove node invalid node ID', { nodeID });
        return;
      }
      const { id, type } = n;

      if (type === 'section') {
        await deleteSection.mutateAsync(id);
        return;
      }

      const updateSections = sections.flatMap((section) => {
        const filtered = section.company_users.flatMap(({ customer, role }) =>
          customer.id === id ? [] : [{ customer_id: customer.id, role_id: role.id }],
        );
        return filtered.length === section.company_users.length
          ? []
          : [
              updateSection.mutateAsync({
                ...section,
                company_users: filtered,
              }),
            ];
      });
      await Promise.all(updateSections);
    },
  });

  const disconnectSection = useMutation({
    mutationFn: async ({
      sectionId,
      invalidateQueries,
    }: {
      sectionId: string;
      invalidateQueries?: boolean;
    }) => {
      const section = sections.find(({ id }) => id === sectionId);
      if (!section) {
        console.warn('failed to disconnect section, section not found', { sectionId });
        return;
      }
      await updateSection.mutateAsync({
        payload: {
          ...section,
          company_users: section.company_users.map(({ customer, role }) => ({
            customer_id: customer.id,
            role_id: role.id,
          })),
          parent_id: companyId,
        },
        options: { invalidateQueries: invalidateQueries },
      });
    },
  });

  const useRemoveEdge = useMutation({
    mutationFn: async ({
      parentNodeId,
      handleId,
      childNodeId,
    }: {
      parentNodeId: string;
      handleId: string;
      childNodeId: string;
    }) => {
      const e = extractEdgeData(parentNodeId, childNodeId);
      if (!e || handleId === openHandleID) {
        console.warn('cannot remove edge, invalid arguments', {
          parentNodeId,
          handleId,
          childNodeId,
        });
        return;
      }

      const { parentId, childId, childType } = e;

      if (childType === 'section') {
        await disconnectSection.mutateAsync({ sectionId: childId, invalidateQueries: true });
        return;
      }

      const parent = sections.find(({ id }) => id === parentId);
      if (!parent) {
        console.warn('cannot remove edge, parent section not found', { parentId });
        return;
      }

      const child = parent.company_users.find(({ customer }) => customer.id === childId);
      if (!child) {
        console.warn('cannot remove edge, child customer not found', { childId });
        return;
      }

      // Add customer to floating customers
      addFloatingCustomer(child.customer);

      // Remove customer from section's company users
      await updateSection.mutateAsync({
        ...parent,
        company_users: parent.company_users.flatMap(({ customer, role }) =>
          customer.id === child.customer.id ? [] : [{ customer_id: customer.id, role_id: role.id }],
        ),
      });
    },
  });

  const useConnect = useMutation({
    mutationFn: async ({
      parentNodeId,
      handleId,
      childNodeId,
    }: {
      parentNodeId: string;
      handleId: string;
      childNodeId: string;
    }) => {
      const e = extractEdgeData(parentNodeId, childNodeId);
      const h = handleId === openHandleID ? ('open' as const) : trimNodeID(handleId);
      if (!e || h === null) {
        console.warn('cannot add connection, invalid arguments', {
          parentNodeId,
          handleId,
          childNodeId,
        });
        return;
      }

      const { parentId, childId, childType } = e;

      const parent = sections.find(({ id }) => id === parentId);
      if (!parent) {
        console.warn('cannot add edge, parent section not found', { parentId });
        return;
      }

      // Determine existing customer or section to remove based on handle
      const sectionToRemove = typeof h === 'object' && h.type === 'section' ? h.id : null;
      const customerToRemove = typeof h === 'object' && h.type === 'customer' ? h.id : null;

      // Company users without the customer to be removed & converted into input format
      const updatedCompanyUsers = parent.company_users.flatMap(({ customer, role }) =>
        customer.id === customerToRemove ? [] : [{ customer_id: customer.id, role_id: role.id }],
      );

      if (childType === 'section') {
        // Find the ID to the *right* of the handle used, use that as our sibling. If the selected
        // ID matches the child, move one further to the right.
        const handleIndex = sectionChildren[parent.id]?.indexOf(sectionToRemove ?? '') ?? -1;
        const candidateSiblingId = sectionChildren[parent.id]?.[handleIndex + 1];
        const sibling_id =
          candidateSiblingId !== childId
            ? candidateSiblingId
            : sectionChildren[parent.id]?.[handleIndex + 2];

        const child = sections.find(({ id }) => id === childId);
        if (!child) {
          console.warn('cannot add edge, child section not found', { childId });
          return;
        }

        // Disconnect existing section if needed
        if (sectionToRemove !== null) {
          await disconnectSection.mutateAsync({
            sectionId: sectionToRemove,
            invalidateQueries: false,
          });
        }

        // Remove existing customer connection if needed
        if (customerToRemove !== null) {
          await updateSection.mutateAsync({
            payload: {
              ...parent,
              company_users: updatedCompanyUsers,
            },
            options: { invalidateQueries: false },
          });
        }

        // Move child to be connected to handle
        await updateSection.mutateAsync({
          ...child,
          company_users: child.company_users.map(({ customer, role }) => ({
            customer_id: customer.id,
            role_id: role.id,
          })),
          parent_id: parent.id,
          sibling_id,
        });
        return;
      }

      const sectionCustomers = sections.flatMap(({ id, company_users }) =>
        id === parent.id ? [] : company_users.map(({ customer }) => customer),
      );
      const sectionIndex = sectionCustomers.findIndex(({ id }) => id === childId);
      const floatingIndex =
        sectionIndex !== -1 ? -1 : floatingCustomers.findIndex(({ id }) => id === childId);

      if (sectionIndex === -1 && floatingIndex === -1) {
        console.warn('cannot add edge, child customer not found', { childId });
        return;
      }

      // Remove from floating list if needed
      if (floatingIndex !== -1) {
        setFloatingCustomers((current) => {
          current.splice(floatingIndex, 1);
          return current;
        });
      }

      // Disconnect existing section if needed
      if (sectionToRemove !== null) {
        await disconnectSection.mutateAsync({
          sectionId: sectionToRemove,
          invalidateQueries: false,
        });
      }

      // Add company user
      await useAddCompanyUser.mutateAsync({
        sectionId: parent.id,
        customer: childId,
      });
    },
  });

  const isValidConnection = useCallback(
    (parentNodeId: string, handleId: string, childNodeId: string) => {
      const edgeData = extractEdgeData(parentNodeId, childNodeId);
      const handle = trimNodeID(handleId);
      if (!edgeData || (!handle && handleId !== openHandleID)) {
        console.warn('failed to validate connection, invalid argmuments', {
          parentNodeId,
          handleId,
          childNodeId,
        });
        return false;
      }

      // If re-using an existing handle, don't count the handle's target
      const allowedChildID = handle?.type === 'section' ? handle.id : null;
      const allowedUserID = handle?.type === 'customer' ? handle.id : null;
      const hasExistingChild =
        sectionChildren[edgeData.parentId]?.some((id) => id !== allowedChildID) ?? false;
      const hasExistingUser =
        sectionUsers[edgeData.parentId]?.some((id) => id !== allowedUserID) ?? false;

      // Ensure that only leaf level sections have users
      if (edgeData.childType === 'section' ? hasExistingUser : hasExistingChild) {
        return false;
      }

      // Ensure that parent is not a descendent of child
      if (edgeData.childType === 'customer') {
        return true;
      }

      const frontier = [edgeData.childId];
      while (true) {
        const current = frontier.pop();
        if (current === edgeData.parentId) {
          return false;
        }
        if (!current) {
          break;
        }
        frontier.unshift(...(sectionChildren[current] ?? []));
      }

      return true;
    },
    [sectionChildren, sectionUsers],
  );

  return {
    floatingCustomers,
    sectionChildren,
    sectionUsers,
    useAddCompanyUser,
    useRemoveNode,
    useRemoveEdge,
    useConnect,
    isValidConnection,
  };
};

export function trimNodeID(nodeID: string): { id: string; type: SectionTreeNodeTypeID } | null {
  const matches = /(section|customer):([\w-]+)/.exec(nodeID);
  const [_, type, id] = matches ?? [];
  // biome-ignore lint/suspicious/noExplicitAny: needed so we can pass string|undefined into sectionTreeNodeIDs.includes
  const isValidType = (str: any): str is SectionTreeNodeTypeID => sectionTreeNodeIDs.includes(str);
  return id && isValidType(type) ? { id, type } : null;
}

function extractEdgeData(
  parentNodeId: string,
  childNodeId: string,
): { parentId: string; childId: string; childType: 'section' | 'customer' } | null {
  const parentId = trimNodeID(parentNodeId);
  const childId = trimNodeID(childNodeId);

  return parentId !== null && childId !== null && parentId.type !== 'customer'
    ? { parentId: parentId.id, childId: childId.id, childType: childId.type }
    : null;
}
